/**
 * nativeNotifications.js
 *
 * Maneja las notificaciones push en la app nativa de Android usando
 * @capacitor/push-notifications (FCM bajo el capó).
 *
 * En web (PWA) este módulo no hace nada — la lógica web sigue igual
 * en firebaseMessaging.js usando el service worker.
 */
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

/** true si la app corre como APK nativa (Android / iOS) */
export const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * Registra la app con FCM y configura todos los listeners.
 *
 * @param {object} callbacks
 * @param {(token: string) => void}   callbacks.onToken       - Se llama cuando FCM entrega el token del dispositivo
 * @param {(notification: object) => void} callbacks.onForeground - App abierta y llega una notificación
 * @param {(data: object) => void}    callbacks.onTap         - Usuario toca la notificación
 * @returns {Promise<boolean>} true si el registro fue exitoso
 */
export const setupNativePush = async ({ onToken, onForeground, onTap } = {}) => {
  if (!isNativePlatform()) return false;

  try {
    // 1️⃣ Pedir permiso al usuario
    const result = await PushNotifications.requestPermissions();
    if (result.receive !== "granted") {
      console.warn("[NativePush] Permiso de notificaciones denegado.");
      return false;
    }

    // 2️⃣ Registrar con el servicio FCM de Google
    await PushNotifications.register();

    // 3️⃣ Token FCM recibido → guardar en Firestore para poder enviar notificaciones
    PushNotifications.addListener("registration", (token) => {
      console.log("[NativePush] Token registrado:", token.value);
      onToken?.(token.value);
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.error("[NativePush] Error al registrar:", err.error);
    });

    // 4️⃣ Notificación recibida con la app en PRIMER PLANO
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("[NativePush] Notificación en primer plano:", notification);
      onForeground?.(notification);
    });

    // 5️⃣ Usuario tocó la notificación (app en background o cerrada)
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification.data || {};
      console.log("[NativePush] Notificación tocada, data:", data);
      onTap?.(data);
    });

    return true;
  } catch (error) {
    console.error("[NativePush] Error en setupNativePush:", error);
    return false;
  }
};

/** Limpia todos los listeners (llamar al desmontar el componente) */
export const removeNativePushListeners = async () => {
  if (!isNativePlatform()) return;
  try {
    await PushNotifications.removeAllListeners();
  } catch {}
};

/**
 * Pide permiso, registra con FCM y devuelve el token como Promise.
 * Útil para el toggle manual en Configuration.jsx.
 * Timeout de 12 s por si FCM tarda.
 */
export const getNativeToken = () => {
  return new Promise(async (resolve, reject) => {
    if (!isNativePlatform()) { resolve(null); return; }

    try {
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== "granted") {
        console.warn("[NativePush] Permiso denegado por el usuario.");
        resolve(null);
        return;
      }

      await PushNotifications.register();

      // El token llega de forma asíncrona vía evento
      let settled = false;
      const listener = await PushNotifications.addListener("registration", (token) => {
        if (settled) return;
        settled = true;
        listener.remove();
        resolve(token.value);
      });

      // Fallback: si no llega en 12 s resuelve null
      setTimeout(() => {
        if (!settled) {
          settled = true;
          listener.remove();
          console.warn("[NativePush] Timeout esperando token FCM.");
          resolve(null);
        }
      }, 12000);

    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Convierte una URL completa o path relativo en un path de React Router.
 * Ej: "https://sit-platform.com/chat/abc123" → "/chat/abc123"
 *     "/chat/abc123"                          → "/chat/abc123"
 */
export const extractPathFromUrl = (url = "") => {
  if (!url) return null;
  try {
    if (url.startsWith("http")) {
      const { pathname } = new URL(url);
      return pathname;
    }
    return url.startsWith("/") ? url : `/${url}`;
  } catch {
    return null;
  }
};
